import { useState, useEffect, useCallback } from "react";
import { batchChannel, type BalanceSnapshot } from "@capture/shared";
import { api } from "@/lib/api-client";
import type { WsClient } from "@/lib/ws-client";
import { useWsRefetch } from "@/hooks/useWsRefetch";
import type {
  AggregateStatus,
  StageBreakdown,
} from "../components/BatchStatusBadge";

// --- Types ---

/**
 * R-147 — Current Step aggregate computed by the API
 * (`apps/api/src/services/batch-current-step.ts`). Mirrors the server
 * shape but kept as a local declaration so the web app doesn't import
 * server modules.
 */
export interface BatchCurrentStep {
  primary: string | null;
  distinctSteps: number;
  breakdown: Record<string, number>;
}

export interface BatchSummary {
  id: string;
  /**
   * R-132 — structured display identifier (e.g. `NOW_20260523_0001`).
   * Null for legacy batches created before WU-7; render `B-<cuid-prefix>`
   * as a muted fallback via {@link formatBatchDisplayId}.
   */
  displayId?: string | null;
  /**
   * R-167 WU-7 — operator-rendered frozen label. UI surfaces prefer this
   * via `preferredDisplayLabel(record)` and fall back to `displayId` when
   * null. Null for legacy batches + tenants without templates configured.
   */
  customDisplayLabel?: string | null;
  tenantId: string;
  name: string;
  source: "upload" | "email" | "scanner" | "directory" | "cross-type-route";
  /** F-CAPTURE-019 — set on batches the route-to-type automation created. */
  routedFromBatchId?: string | null;
  status: string;
  /** R-214 — docs currently assigned to an operator; drives the work-item busy/ready label. */
  assignedCount?: number;
  /** R-133 — derived from constituent doc statuses; drives the keyer-facing badge. */
  aggregateStatus: AggregateStatus;
  /** R-133 — per-stage bucket counts for the "5 ready · 0 scanning · …" line. */
  stageBreakdown: StageBreakdown;
  /**
   * R-147 — dominant active pipeline step + per-step counts. `primary` is
   * null when every doc has exported (no active work) or the batch has
   * zero docs. Drives the Current Step column on the list view.
   */
  currentStep?: BatchCurrentStep | null;
  jobTypeId: string;
  jobTypeName: string;
  documentCount: number;
  completedCount: number;
  /**
   * R-191 — partial-credit pipeline progress percent (0..100), computed
   * server-side by `computeBatchProgressPct`. The BatchListPage progress
   * bar reads this directly instead of the binary
   * `completedCount / documentCount` ratio, which was stuck at 0% for any
   * mid-flight batch (R-166 had introduced the helper but only on the
   * detail page). Optional because pre-R-191 API responses don't include
   * it — UI treats absence as 0%.
   */
  progressPct?: number;
  controlTotal: number | null;
  processingMode: string;
  /**
   * R-261 — count of open rescan-requests on this batch. Drives the
   * "Rescan ×N" badge on the list row. 0 when there are no open requests
   * (field may be absent on pre-R-261 API responses — treat absence as 0).
   */
  openRescanCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface BatchDetail {
  id: string;
  /** R-132 — see BatchSummary.displayId. */
  displayId?: string | null;
  /** R-167 WU-7 — see BatchSummary.customDisplayLabel. */
  customDisplayLabel?: string | null;
  tenantId: string;
  name: string;
  source: "upload" | "email" | "scanner" | "directory" | "cross-type-route";
  status: string;
  aggregateStatus: AggregateStatus;
  stageBreakdown: StageBreakdown;
  /** R-147 — same Current Step shape as BatchSummary. */
  currentStep?: BatchCurrentStep | null;
  jobTypeId: string;
  jobTypeName: string;
  processingMode: string;
  /**
   * E-026 — the job type's classification mode ("off" | "source_declared" |
   * "learned"), surfaced so the doc table can render an honest label for
   * docs with a null `classification` on a classification-off job (that's
   * the expected default form resolution, not a problem state). Optional
   * because pre-E-026 API responses don't include it — treat absence as
   * unknown (falls back to the classification-expected "Unclassified" copy).
   */
  jobTypeClassificationMode?: string | null;
  controlTotal: number | null;
  documentCount: number;
  completedCount: number;
  /**
   * R-155 — resolved auto-export gate for this batch's tenant + job-type.
   *   true  → batch will wait for manual "Release for Export"
   *   false → batch will auto-fire on export-ready (subject to destinations)
   * Optional because pre-R-155 API responses don't include it — client
   * treats absence as `true` (existing manual-only behaviour).
   */
  effectiveManualRelease?: boolean;
  /**
   * R-155 — count of active export destinations for the batch's job type.
   * When 0, the R-145 pre-flight check blocks BOTH the auto-fire path AND
   * the manual release; the UI surfaces a dedicated "no destinations"
   * guidance state in that case.
   */
  activeExportDestinationCount?: number;
  /**
   * R-155 — raw per-job-type override (null = inherit tenant default,
   * true = manual required, false = auto-fire). Surfaced so admin tooling
   * can render the tri-state correctly.
   */
  jobTypeRequireManualRelease?: boolean | null;
  /** R-155 — tenant-wide default. Surfaced for tooling/diagnostics. */
  tenantRequireManualRelease?: boolean;
  createdAt: string;
  updatedAt: string;
  documents: BatchDocument[];
  pipelineProgress: PipelineStepProgress[];
  /**
   * R-261 — open rescan-requests on this batch. Present only when there are
   * open requests (pre-R-261 API responses omit the field — treat absence as
   * empty array via `batch.rescanRequests ?? []`).
   */
  rescanRequests?: Array<{
    id: string;
    documentId: string | null;
    note: string | null;
    context: { filename?: string; pageNumbers?: number[] } | null;
    requestedBy: string;
    createdAt: string;
  }>;
  /**
   * SP2 §8 (Task 9) — the batch's job type's capture surface. Optional
   * because pre-Task-9 API responses omit it — UI treats absence as
   * "standard" (TransactionsPanel renders nothing in that case).
   */
  surface?: string;
  /**
   * SP2 §8 (Task 9) — ordered remittance transactions (empty for
   * standard-surface batches and pre-Task-9 API responses).
   */
  transactions?: Transaction[];
}

export interface BatchDocument {
  id: string;
  /** R-132 — derived document display identifier (e.g. `NOW_20260523_0001_01`). */
  displayId?: string | null;
  /** R-167 WU-7 — frozen per-document label written by the WU-4 allocator. */
  customDisplayLabel?: string | null;
  filename: string;
  classification: string | null;
  /** R-247 5b-3 — source that produced the classification ("fingerprint" | "manual" | etc.). */
  classificationSource: string | null;
  /** R-247 5b-3b — keyer-escalation context; category "misidentified_type" gates the reclassify control. */
  escalationContext?: { category?: "misidentified_type" } | null;
  currentStep: string;
  status: string;
  assignedTo: string | null;
  assignedToName: string | null;
  priority: number;
  /** R-131 — free-form human-readable reason (e.g. `ocr-failed: ...`). */
  holdReason?: string | null;
  /** R-131 — machine-readable upstream code (e.g. `TENANT_HEADER_MISSING`). */
  errorCode?: string | null;
  /** R-131 — full structured upstream error payload, if any. */
  errorDetails?: unknown;
}

export interface PipelineStepProgress {
  step: string;
  label: string;
  count: number;
}

/** SP2 §8 (Task 11) — member document of a Transaction, as embedded in
 *  GET /batches/:id (`select: { id, displayId, filename, classification }`
 *  in apps/api/src/routes/batches/index.ts). */
export interface TransactionDocument {
  id: string;
  displayId: string | null;
  filename: string;
  classification: string | null;
}

/** SP2 §6 — the hardwired per-transaction balancer's output shape
 *  (packages/shared/src/remittance/balance.ts `BalanceSnapshot`), persisted
 *  as JSON on `Transaction.balanceSnapshot` and returned verbatim by the API.
 *  Aliased from @capture/shared (single source of truth) — if the shared
 *  verdict union changes, web picks it up instead of drifting silently. */
export type TransactionBalanceSnapshot = BalanceSnapshot;

/**
 * SP2 §8 (Task 9/11) — one remittance transaction (a grouped payment +
 * applied-coupon set) as returned by GET /batches/:id for surface:"remittance"
 * batches. Single definition, imported by both this hook (typing
 * `BatchDetail.transactions`) and TransactionsPanel (Task 11) so the two
 * never drift.
 */
export interface Transaction {
  id: string;
  ordinal: number;
  state: "formed" | "unarmed" | "orphan";
  balanceSnapshot: TransactionBalanceSnapshot | null;
  overriddenBy: string | null;
  overriddenAt: string | null;
  overrideReason: string | null;
  documents: TransactionDocument[];
}

/** R-264 5e-2 — single failed balance rule entry from the API's flat DTO. */
export interface BalanceFailureEntry {
  ruleId: string;
  ruleName: string;
  message: string;
  declared: unknown;
  actual: unknown;
  severity: "error" | "warning";
  overrideBehavior: string;
  trigger?: string;
  overridden?: boolean;
  declaredFieldRef?: string | null;
}

/** R-264 5e-2 — flat reconciliation DTO from GET /batches/:id/reconciliation
 *  and POST /batches/:id/reconciliation/recheck. Replaces the pre-existing
 *  nested shape (pre-existing drift fixed here — see Task 5 / Task 13). */
export interface ReconciliationResult {
  batchId: string;
  passed: boolean;
  waiting: boolean;
  totalDocuments: number;
  completedDocuments: number;
  expectedTotal: number | null;
  actualTotal: number;
  discrepancy: number;
  failures: ReconciliationFailure[];
  balanceFailures: BalanceFailureEntry[];
  status: string | null;
}

export interface ReconciliationFailure {
  type: string;
  message: string;
  details: string | null;
}

interface BatchListResponse {
  items: BatchSummary[];
  total: number;
}

export interface BatchFilters {
  /** F-CAPTURE-009 — server-side sort key (whitelisted by the API) and direction. */
  sort?: string;
  dir?: "asc" | "desc";
  status?: string;
  jobTypeId?: string;
  search?: string;
  /**
   * F-065 — server-side lifecycle scope. Default (absent) returns ACTIVE
   * batches only; "history" returns fully-terminal batches; "all" skips the
   * partition (both). BatchListPage passes "all" for ?state= deep links so
   * fully-complete batches are fetchable at all.
   */
  scope?: "history" | "all";
  /**
   * F-094 — server-side terminal doc-state filter (error | complete). The
   * API filters BEFORE limit/offset and returns the filtered `total`, so
   * pagination over a ?state= deep link is honest. (Previously applied
   * client-side over server pages, which made "Showing X–Y of Z" lie.)
   */
  state?: string;
  limit?: number;
  offset?: number;
}

// --- List hook ---

// Live-batch-surfaces Task 3 — every WS event that can change a batch row
// (its stage breakdown, aggregate status, current step, or the row set
// itself). All arrive on the auto-subscribed `queue:<tenantId>` channel.
// `batch_ingested` (worker ingest-batch.ts) previously had NO web handler
// anywhere, so a new batch never appeared without a poll tick.
const BATCH_LIST_WS_EVENTS = [
  "batch_ingested",
  "batch_released",
  "queue_update",
  "document_advance",
  "export_status",
] as const;

export function useBatches(filters?: BatchFilters, wsClient?: WsClient | null) {
  const [items, setItems] = useState<BatchSummary[]>([]);
  const [total, setTotal] = useState(0);
  // R-182 — stale-while-revalidate. `isLoading` is the *blocking* initial-load
  // flag: it starts true and is only ever set false (never back to true), so a
  // poll-driven refetch never re-shows the page skeleton. `isRefreshing` tracks
  // background refetches for an optional non-blocking indicator. Pages gate the
  // skeleton on initial load only (`isLoading && items.length === 0`).
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    // Do NOT clear `items` and do NOT flip `isLoading` true on refetch — keep
    // prior rows mounted while the new data fetches in the background.
    setIsRefreshing(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters?.status && filters.status !== "all") params.set("status", filters.status);
      if (filters?.jobTypeId) params.set("jobTypeId", filters.jobTypeId);
      if (filters?.search) params.set("search", filters.search);
      if (filters?.scope) params.set("scope", filters.scope);
      if (filters?.state) params.set("state", filters.state);
      if (filters?.limit) params.set("limit", String(filters.limit));
      if (filters?.offset) params.set("offset", String(filters.offset));
      if (filters?.sort) {
        params.set("sort", filters.sort);
        params.set("dir", filters.dir ?? "asc");
      }
      const qs = params.toString();
      const data = await api.get<BatchListResponse>(`/batches${qs ? `?${qs}` : ""}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load batches");
    } finally {
      setIsLoading(false); // first load done; idempotent thereafter.
      setIsRefreshing(false);
    }
  }, [filters?.status, filters?.jobTypeId, filters?.search, filters?.scope, filters?.state, filters?.limit, filters?.offset, filters?.sort, filters?.dir]);

  useEffect(() => { load(); }, [load]);

  useWsRefetch(wsClient ?? null, BATCH_LIST_WS_EVENTS, load);

  return { items, total, isLoading, isRefreshing, error, refresh: load };
}

// --- Detail hook ---

// Live-batch-surfaces Task 4 (narrowed in /simplify) — the detail view fetches
// ONE batch, so its hot-path trigger is the batch-scoped channel, not the
// tenant-wide firehose: every workflow-engine advance co-publishes a tenant
// `document_advance`/`queue_update` AND a scoped BATCH_PROGRESS on
// `batch:<id>`, so subscribing both meant 2-3 duplicate GETs per advance plus
// a refetch every time an UNRELATED batch moved. Only the scoped event plus
// the rare tenant-wide lifecycle events (release, export landing) stay; the
// 30s safety-net poll covers unpaired mutations (e.g. transaction repair).
const BATCH_DETAIL_WS_EVENTS = [
  "BATCH_PROGRESS",
  "batch_released",
  "export_status",
] as const;

export function useBatch(id: string | undefined, wsClient?: WsClient | null) {
  const [batch, setBatch] = useState<BatchDetail | null>(null);
  // R-182 — stale-while-revalidate (see useBatches). `isLoading` is one-way;
  // `isRefreshing` tracks background refetches. The 8s BatchDetail poll keeps
  // the doc table mounted instead of flashing the page skeleton each tick.
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    // Do NOT clear `batch` and do NOT flip `isLoading` true on refetch.
    setIsRefreshing(true);
    setError(null);
    try {
      const data = await api.get<BatchDetail>(`/batches/${id}`);
      setBatch(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load batch");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // No channel until the route param resolves — WsClient refcounts desired
  // channels, so the mount/unmount subscribe pair here is StrictMode-safe.
  useWsRefetch(wsClient ?? null, BATCH_DETAIL_WS_EVENTS, load, id ? batchChannel(id) : undefined);

  return { batch, isLoading, isRefreshing, error, refresh: load };
}

// --- Mutation hooks ---

export function useReleaseBatch() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const release = useCallback(async (id: string) => {
    setIsSubmitting(true);
    try {
      return await api.post<{ success: boolean }>(`/batches/${id}/release`);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { release, isSubmitting };
}

/**
 * R-174 — Response shape for POST /batches/:id/reprocess. The endpoint
 * was made destructive in R-176 (clears ALL operator-keyed values before
 * re-enqueuing OCR); `clearedFieldCount` lets the UI surface "K keyed
 * values discarded" so operators understand the cost in the success toast.
 */
export interface ReprocessBatchResponse {
  success: boolean;
  documentCount: number;
  clearedFieldCount: number;
  message: string;
}

export function useReprocessBatch() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reprocess = useCallback(async (id: string) => {
    setIsSubmitting(true);
    try {
      return await api.post<ReprocessBatchResponse>(
        `/batches/${id}/reprocess`,
      );
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { reprocess, isSubmitting };
}

export function useDeleteBatch() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const deleteBatch = useCallback(async (id: string) => {
    setIsSubmitting(true);
    try {
      await api.delete(`/batches/${id}`);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { deleteBatch, isSubmitting };
}

// --- Reconciliation hook ---

export function useBatchReconciliation(id: string | undefined) {
  const [reconciliation, setReconciliation] = useState<ReconciliationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<ReconciliationResult>(`/batches/${id}/reconciliation`);
      setReconciliation(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load reconciliation");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // R-264 5e-2 — recheck: persisting variant of the reconciliation (POST); updates
  // the panel in-place without a full page refresh.
  const recheck = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.post<ReconciliationResult>(`/batches/${id}/reconciliation/recheck`, {});
      setReconciliation(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Re-check failed");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return { reconciliation, isLoading, error, refresh: load, recheck };
}

// R-264 5e-2 — batches-local action hooks for balance failure management.
// Note: cross-feature duplication with classify's client fns is intentional —
// each feature keeps its own client here rather than sharing; /simplify adjudicates.

/** Override a soft balance failure rule for a batch. */
export function useOverrideBalanceFailure() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const override = useCallback(async (batchId: string, ruleId: string) => {
    setIsSubmitting(true);
    try {
      return await api.post(`/batches/${batchId}/balance-failures/${ruleId}/override`, {});
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { override, isSubmitting };
}

/**
 * SP2 §8 (Task 11) — supervisor override of a single transaction's balance
 * failure (Task 8's POST /:id/transactions/:transactionId/override, gated
 * server-side on the bare `remittance.balance` action). Distinct from
 * useOverrideBalanceFailure above, which overrides a rule-engine balance
 * rule for the whole batch, not a per-transaction verdict.
 */
export function useOverrideTransactionBalance() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const override = useCallback(async (batchId: string, transactionId: string, reason: string) => {
    setIsSubmitting(true);
    try {
      return await api.post<{ transaction: Transaction }>(
        `/batches/${batchId}/transactions/${transactionId}/override`,
        { reason },
      );
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { override, isSubmitting };
}

/** Manually clear a balance_review batch (soft-only; 409s if hard stands). */
export function useClearBalanceReview() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clear = useCallback(async (batchId: string) => {
    setIsSubmitting(true);
    try {
      return await api.post<{ ok: boolean }>(`/batches/${batchId}/clear-balance-review`, {});
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { clear, isSubmitting };
}
